import React from "react";
import { format } from "date-fns";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Printer, Download, CheckCircle } from "lucide-react";

const ViewInvoiceModal = ({
  invoice,
  onClose,
  currentUser,
  formatPrice,
  onPrint,
  onDownloadPDF,
  onValidate,
}) => {
  return (
    <Dialog open={!!invoice} onOpenChange={() => onClose()}>
      <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg" data-testid="view-invoice-modal">
        <DialogHeader>
          <DialogTitle>Facture {invoice?.invoice_number}</DialogTitle>
        </DialogHeader>
        {invoice && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="text-slate-400">Client:</span> {invoice.customer_name}</div>
              <div><span className="text-slate-400">Date:</span> {format(new Date(invoice.created_at), "dd/MM/yyyy HH:mm")}</div>
              <div><span className="text-slate-400">Agent:</span> {invoice.created_by || "-"}</div>
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Statut:</span>
                {invoice.validation_status === "validated" ? (
                  <Badge className="bg-green-500/20 text-green-400">✓ Validée par {invoice.validated_by}</Badge>
                ) : (
                  <Badge className="bg-yellow-500/20 text-yellow-400">⏳ En attente de validation</Badge>
                )}
              </div>
            </div>
            <div className="border-t border-slate-700 pt-4">
              {invoice.items?.map((item, idx) => (
                <div key={idx} className="flex justify-between py-1 text-sm">
                  <span>{item.name} x{item.quantity}</span>
                  <span className="text-amber-400">{formatPrice(item.price * item.quantity)} F</span>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-700 pt-4 space-y-1">
              <div className="flex justify-between">
                <span className="text-slate-400">Sous-total</span>
                <span>{formatPrice(invoice.subtotal)} F</span>
              </div>
              {invoice.discount > 0 && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Remise ({invoice.discount}%)</span>
                  <span className="text-green-400">-{formatPrice(invoice.discount_amount)} F</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t border-slate-700">
                <span>TOTAL</span>
                <span className="text-amber-500">{formatPrice(invoice.total)} FCFA</span>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {/* Print/Download buttons - Manager/Admin only */}
              {(currentUser?.role === "manager" || currentUser?.role === "admin") ? (
                <>
                  <Button
                    onClick={() => onPrint(invoice)}
                    variant="outline"
                    className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
                    data-testid="view-invoice-print-btn"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Ticket 80mm
                  </Button>
                  <Button
                    onClick={() => onDownloadPDF(invoice)}
                    className="bg-amber-500 hover:bg-amber-600"
                    data-testid="view-invoice-pdf-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    PDF A4
                  </Button>
                </>
              ) : (
                <div className="col-span-2 text-center py-2">
                  <p className="text-slate-500 text-sm">Contactez la responsable op. & log pour imprimer</p>
                </div>
              )}
            </div>
            {invoice.validation_status !== "validated" && currentUser?.role === "admin" && (
              <Button
                onClick={() => { onValidate(invoice.id); onClose(); }}
                className="w-full bg-green-600 hover:bg-green-700"
                data-testid="view-invoice-validate-btn"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Valider ce facture
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewInvoiceModal;
